import * as React from "react";
import { useRouter } from "next/router";

import { Grid } from "ui/grid/Grid";
import { RoundedInput } from "ui/roundedInput/RoundedInput";
import { GoogleAutoComplete } from "ui/googleAutoComplete/GoogleAutoComplete";
import { SplitScreenContainer } from "ui/splitScreenContainer/SplitScreenContainer.js";

import { CollectFunnelContext } from "components/collectFunnel/CollectFunnelProvider";
import { SET_ADDRESS } from "components/collectFunnel/constants";
import { STEP_INFORMATION, STEP_PRODUCT_CHOICE_URL } from "components/collectFunnel/constants";

import { Typography, makeStyles, Button } from "@material-ui/core";
import PlaceIcon from "@material-ui/icons/Place";
import CheckCircle from "components/collectFunnel/assets/checkCircle.svg";
import Timeline from "@material-ui/lab/Timeline";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import useProductTypes from "hooks/useProductTypes";

export const CollectFunnelAddressFormStep = () => {
  const classes = useStyle();
  const [state, dispatch] = React.useContext(CollectFunnelContext);
  const [address, setAddress] = React.useState(state.address ? state.address.formatted_address : "");
  const [validatedAddress, setValidatedAddress] = React.useState(state.address ? state.address.formatted_address : "");
  const router = useRouter();
  const productTypes = useProductTypes({ is_collection_enabled: true });

  const [addressError, setAddressError] = React.useState(
    state.address?.name ? "Merci de sélectionner votre adresse dans la liste de propositions" : null
  );

  const handleNextStep = React.useCallback(() => {
    if (!validatedAddress) {
      setAddressError("Merci de sélectionner votre adresse dans la liste de propositions");
    } else {
      router.push(STEP_PRODUCT_CHOICE_URL, undefined, { shallow: true });
    }
  }, [router, validatedAddress]);

  const handleChange = React.useCallback(
    ({ target: { value } }) => {
      setAddressError(null);
      setAddress(value);
      if (validatedAddress) {
        setValidatedAddress("");
      }
    },
    [validatedAddress]
  );

  const handleChangeAddress = React.useCallback(
    (value) => {
      if (!value.address_components) {
        setAddressError("Merci de sélectionner votre adresse dans la liste de propositions");
      } else if (!value.address_components.find((i) => i.types.includes("street_number"))) {
        setAddressError("Merci de choisir un numéro de rue");
      } else {
        setAddressError(null);
        setAddress(value.formatted_address);
        setValidatedAddress(value.formatted_address);
        dispatch({
          type: SET_ADDRESS,
          data: {
            address: value,
            postalCode: value.address_components.find((component) => component.types.includes("postal_code"))
              ?.long_name,
          },
        });
      }
    },
    [dispatch]
  );

  const renderContent = () => (
    <div className={classes.contentContainer}>
      <Typography className={classes.title} variant="h2" color="secondary">
        Murfy vient récupérer gratuitement chez vous votre appareil usagé :
      </Typography>
      <br />
      <div className={classes.title}>Prenez rendez-vous en 3 étapes. </div>
      <br />
      <Timeline className={classes.timeline}>
        {STEP_INFORMATION.map((information, index) => (
          <TimelineItem key={index}>
            <TimelineSeparator>
              <TimelineDot className={classes.dot} color="primary">
                <Typography className={classes.stepNumber} variant="body2" color="secondary">
                  {index + 1}
                </Typography>
              </TimelineDot>
              {index !== STEP_INFORMATION.length - 1 && <TimelineConnector className={classes.connector} />}
            </TimelineSeparator>
            <TimelineContent>{information.title}</TimelineContent>
          </TimelineItem>
        ))}
        C’est parti ! Pour prendre rendez-vous, indiquez-nous votre adresse :
      </Timeline>
      <GoogleAutoComplete onChange={handleChangeAddress}>
        <RoundedInput
          fullWidth
          value={address}
          onChange={handleChange}
          placeholder="Saisissez votre adresse"
          InputProps={{ endAdornment: <PlaceIcon style={{ color: "#A4E7DB" }} /> }}
        />
      </GoogleAutoComplete>
      <Typography variant="caption" color="error" className={classes.error}>
        {addressError}
      </Typography>
      <Grid container item justify="flex-end" className={classes.actionContainer}>
        <Grid item>
          <Button disabled={addressError || !address} variant="contained" color="primary" onClick={handleNextStep}>
            Prendre rendez-vous
          </Button>
        </Grid>
      </Grid>
      <Grid container className={classes.tipsContainer}>
        <Typography className={classes.title}>Nous récupérons</Typography>
        <Grid container direction="row" spacing={2} alignItems="center">
          {productTypes?.map((productType) => {
            return (
              <Grid key={productType.id} item xs={12} sm="auto">
                <Typography variant="body2" className={classes.tipsContent}>
                  <CheckCircle className={classes.checkcircle} />
                  {productType.name}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </div>
  );

  return (
    <SplitScreenContainer
      background="/rendez-vous-collecte/unloading.png"
      renderContent={renderContent}
      reverseContainer
      hideImageOnTablet
      simpleMobileDisplay
    />
  );
};

const useStyle = makeStyles((theme) => ({
  error: {
    marginLeft: theme.spacing(2),
  },
  content: { color: "white", display: "flex", marginBottom: theme.spacing(1) },
  box: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(0),
    },
  },
  contentContainer: { maxWidth: 580, margin: "auto" },
  contentText: {
    color: theme.palette.secondary.main,
    margin: theme.spacing(5, 0),
    fontSize: 19,
  },
  titleInfo: {
    color: "white",
    fontWeight: 700,
  },
  title: {
    fontWeight: 700,
  },
  timeline: {
    padding: theme.spacing(0),
  },
  stepNumber: {
    margin: "auto",
  },
  dot: {
    width: theme.spacing(4),
    margin: theme.spacing(0),
    boxShadow: "none",
  },
  connector: {
    backgroundColor: theme.palette.primary.main,
  },
  actionContainer: {
    marginTop: theme.spacing(3),
  },
  checkcircle: {
    paddingTop: theme.spacing(0),
    marginRight: theme.spacing(1),
  },
  tipsContainer: {
    backgroundColor: theme.palette.containerGrey,
    borderRadius: 10,
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  tipsContent: {
    fontSize: 14,
    display: "inline-flex",
    verticalAlign: "middle",
  },
}));
